import React, { memo, useMemo } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import styles from "./machines-list-row.module.scss";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import sourceIcon from "@src/assets/svg/source-icon.svg";
import dashboardIcon from "@src/assets/svg/dashboard-icon.svg";
import conditionMonitoringIcon from "@src/assets/svg/condition-monitoring-icon.svg";
import MachineSettingsIcon from "@src/assets/svg/machine-settings-icon.svg";
import shotDataTableIcon from "@src/assets/svg/short-data-table-icon.svg";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { MachinesListRemoteSupportRequestedButton } from "../machines-remote-support-requested-button/machines-remote-support-requested-button";
import { MachinesListRemoteSupportActiveButton } from "../machines-remote-support-active-button/machines-remote-support-active-button";
import {
  MachinesListDataTypes,
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { MachinesCancelRequestDisconnectSupportModal } from "@src/components/organisms/modals/machines-cancel-request-disconnect-support-modal/machines-cancel-request-disconnect-support-modal";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import { MachinesCancelServiceTechnicianManagerModal } from "@src/components/organisms/modals/machines-cancel-service-technician-modal/machines-cancel-service-technician-modal";
import machinesRemoteSupportIcon from "@src/assets/svg/machines-remote-support.svg";
import treeStructureIcon from "@src/assets/svg/tree-structure-icon.svg";
import { RenderIfUserPermissionsRulesMatch } from "../render-if-user-permissions-rules-match/render-if-user-permissions-rules-match";
import { MachinesCancelOperatorTechnicianManagerModal } from "@src/components/organisms/modals/machines-cancel-operator-technician-modal/machines-cancel-operator-technician-modal";
import { useMachineListRowHook } from "./use-machines-list-row-hook";
import { MachinesListMachineTypes } from "@src/types/gateways-with-machines";
import { TicketSupportForMachineRowIconButton } from "@src/components/atoms/ticket-support-for-machine-row-icon-button/ticket-support-for-machine-row-icon-button";
import { EditMachineName } from "../edit-machine-name/edit-machine-name";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { RenderIfUserHasPermission } from "../render-if-user-has-permission/render-if-user-has-permission";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

const cssClassName = "machines-list-row";

interface MachineListRowPropsTypes {
  machine: MachinesListMachineTypes;
  companyName: string;
  showSTSupport?: boolean;
  showOTSupport?: boolean;
  onActiveOtherUserVisibilityChange?: (
    machineId: string,
    isVisible: boolean,
  ) => void;
  selectedMachineId?: string;
}

export const MachineListRow = memo(
  ({
    machine,
    companyName,
    showSTSupport,
    showOTSupport,
    onActiveOtherUserVisibilityChange,
    selectedMachineId,
  }: MachineListRowPropsTypes) => {
    const targetMachineId = selectedMachineId;
    const machineIdentity = machine.machine_id;
    const {
      isConnected,
      isRemoteActionsDisabled,
      remoteActionsDisabledTooltip,
      isClosingState,
      isDurationExpired,
      setLocalRemainingDuration,
      texts,
      modalTexts,
      showSTCancelModal,
      showOTCancelModal,
      handleRequestButtonClick,
      openSTCancelRequest,
      confirmCancelRemoteAccessRequest,
      confirmInterruptRemoteAccessRequest,
      closeOTCancelModal,
      closeSTCancelModal,
      confirmCancelRemoteAccessRequestLoading,
      stRequestClickLoading,
      otRequestClickLoading,
      handleOTActiveButtonClick,
      showDisconnectSupportModal,
      closeDisconnectSupportModal,
      disconnectTexts,
      stActiveClickLoading,
      otActiveClickLoading,
      openOTCancelRequest,
      handleSTActiveButtonClick,
      clickTreeStructureLink,
      clickDashboardLink,
      clickConditionMonitoringLink,
      isUserConditionMonitoringViewer,
      displayName,
      isCurrentUserSessionOwner,
      isCurrentUserSessionViewer,
      isManagerOwner,
      canRequestorCancelActiveInProgress,
      canApproverCancelActiveInProgress,
      clickMachineSettingsLink,
      clickShotDataTableLink,
      enterScreensharing,
      disconnectActiveSession,
      disconnecting,
      hasOpcUaSkill,
      hasVncSkill,
      actionDisabledReason,
      showOTA,
      showShotDataTable,
    } = useMachineListRowHook(machine);

    const handleCopyMachineId = async (
      event: React.MouseEvent | React.KeyboardEvent,
    ) => {
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(machine.machine_id);
        successSnackbar(texts.successMessage);
      } catch {
        errorSnackbar(texts.errorMessage);
      }
    };

    const opcUaDataSourceId = machine.opc_ua_data_source_id || "";
    const machineForOpcUaActions = {
      ...machine,
      source_id: opcUaDataSourceId,
    } as MachinesListDataTypes;

    /// Reference to the modal entry point in the DOM, used for rendering modals via portals
    const modalEntryPointReference = document.getElementById(
      "modals-entry-point",
    ) as HTMLElement;

    const remoteActionProps = useMemo(
      () => ({
        disableAllActions: isRemoteActionsDisabled || !hasVncSkill,
        tooltip: !hasVncSkill
          ? texts.missingMachineSkillTooltip
          : remoteActionsDisabledTooltip,
      }),
      [
        hasVncSkill,
        isRemoteActionsDisabled,
        remoteActionsDisabledTooltip,
        texts.missingMachineSkillTooltip,
      ],
    );

    const handleOpcUaActionClick = (
      e: React.MouseEvent | React.KeyboardEvent,
      action: (
        event: React.MouseEvent | React.KeyboardEvent,
        row: MachinesListDataTypes,
      ) => void,
    ) => {
      e.stopPropagation();
      action(e, machineForOpcUaActions);
    };

    const OTactionCell = useMemo(() => {
      const isPrivateSession = machine?.visibility === "private";

      if (isClosingState) {
        return (
          <MachinesListRemoteSupportRequestedButton
            buttonText={texts.closing}
            onCancel={openOTCancelRequest}
            status={machine?.status as MachinesListSupportSessionsStatusTypes}
            {...remoteActionProps}
            isClosingState
          />
        );
      }

      if (
        (machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
          machine.status === MachinesListSupportSessionsStatusTypes.OPEN) &&
        !isClosingState &&
        !isDurationExpired &&
        machine?.approved_duration
      ) {
        return (
          <MachinesListRemoteSupportActiveButton
            onViewDetails={handleOTActiveButtonClick}
            onCancel={openOTCancelRequest}
            requestedDuration={machine?.remaining_duration}
            setLocalRemainingDuration={setLocalRemainingDuration}
            buttonText={texts.active}
            loading={otActiveClickLoading}
            zitadelRole={MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN}
            status={machine?.status}
            {...remoteActionProps}
            isPrivateSession={isPrivateSession}
            showCancelIcon={canApproverCancelActiveInProgress}
          />
        );
      }
      if (
        machine.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS ||
        machine.isRequested
      ) {
        if (isManagerOwner || isCurrentUserSessionViewer) {
          return (
            <MachinesListRemoteSupportRequestedButton
              buttonText={texts.viewing}
              onCancel={openOTCancelRequest}
              loading={otActiveClickLoading}
              status={machine?.status as MachinesListSupportSessionsStatusTypes}
              {...remoteActionProps}
              tooltip={remoteActionProps.tooltip || texts.viewingTooltip}
              showCancelIcon={canApproverCancelActiveInProgress}
            />
          );
        }

        const inProgressRequestedButtonText = isPrivateSession
          ? texts.inProgressPrivate
          : texts.joinSession;
        const inProgressTooltip = isPrivateSession
          ? texts.inProgressPrivateTooltip
          : texts.joinSessionTooltip;
        const canJoinInProgress = !isPrivateSession;

        return (
          <MachinesListRemoteSupportRequestedButton
            buttonText={inProgressRequestedButtonText}
            onViewDetails={
              canJoinInProgress ? handleOTActiveButtonClick : undefined
            }
            onCancel={openOTCancelRequest}
            loading={otActiveClickLoading}
            status={machine?.status as MachinesListSupportSessionsStatusTypes}
            {...remoteActionProps}
            tooltip={remoteActionProps.tooltip || inProgressTooltip}
            allowInProgressViewDetails={canJoinInProgress}
            showJoinSessionIcon={canJoinInProgress}
            showCancelIcon={canApproverCancelActiveInProgress}
          />
        );
      }
      if (
        machine.status === MachinesListSupportSessionsStatusTypes.REQUESTED ||
        machine.isRequested
      ) {
        return (
          <MachinesListRemoteSupportRequestedButton
            onViewDetails={(e) =>
              handleRequestButtonClick(
                e,
                MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
              )
            }
            onCancel={openOTCancelRequest}
            buttonText={texts.incomingRequested}
            {...remoteActionProps}
            loading={otRequestClickLoading}
          />
        );
      }
    }, [
      isClosingState,
      isDurationExpired,
      machine.status,
      machine?.approved_duration,
      machine?.remaining_duration,
      machine?.visibility,
      machine.isRequested,
      setLocalRemainingDuration,
      handleOTActiveButtonClick,
      openOTCancelRequest,
      texts.closing,
      texts.active,
      texts.inProgressPrivate,
      texts.joinSession,
      texts.inProgressPrivateTooltip,
      texts.joinSessionTooltip,
      texts.incomingRequested,
      isCurrentUserSessionViewer,
      isManagerOwner,
      texts.viewing,
      texts.viewingTooltip,
      otActiveClickLoading,
      handleRequestButtonClick,
      otRequestClickLoading,
      canApproverCancelActiveInProgress,
      remoteActionsDisabledTooltip,
      remoteActionProps,
    ]);
    const STactionCell = useMemo(() => {
      const isPrivateSession = machine?.visibility === "private";

      if (isClosingState) {
        return (
          <MachinesListRemoteSupportRequestedButton
            buttonText={texts.closing}
            onCancel={openSTCancelRequest}
            status={machine?.status as MachinesListSupportSessionsStatusTypes}
            {...remoteActionProps}
            isClosingState
          />
        );
      }

      if (
        (machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
          machine.status === MachinesListSupportSessionsStatusTypes.OPEN) &&
        !isClosingState &&
        !isDurationExpired &&
        machine?.remaining_duration
      ) {
        return (
          <MachinesListRemoteSupportActiveButton
            onViewDetails={handleSTActiveButtonClick}
            onCancel={openSTCancelRequest}
            requestedDuration={machine?.remaining_duration}
            setLocalRemainingDuration={setLocalRemainingDuration}
            buttonText={texts.active}
            loading={stActiveClickLoading}
            zitadelRole={
              MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
            }
            status={machine?.status}
            {...remoteActionProps}
            isCurrentUserSessionOwner={isCurrentUserSessionOwner}
            isPrivateSession={isPrivateSession}
            onActiveOtherUserViewChange={(isVisible) =>
              onActiveOtherUserVisibilityChange?.(machineIdentity, isVisible)
            }
            showCancelIcon={canRequestorCancelActiveInProgress}
            onEnterScreensharing={(settings) =>
              enterScreensharing(
                MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
                settings,
              )
            }
            onDisconnect={disconnectActiveSession}
            disconnecting={disconnecting}
          />
        );
      }
      if (
        machine.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS ||
        machine.isRequested
      ) {
        const canJoinInProgress =
          !isPrivateSession &&
          !isCurrentUserSessionOwner &&
          !isCurrentUserSessionViewer;

        let inProgressRequestedButtonText;
        let inProgressTooltip;

        if (isCurrentUserSessionOwner) {
          inProgressRequestedButtonText = texts.inProgressSession;
          inProgressTooltip = "";
        } else if (isCurrentUserSessionViewer) {
          inProgressRequestedButtonText = texts.viewing;
          inProgressTooltip = texts.viewingTooltip;
        } else if (isPrivateSession) {
          inProgressRequestedButtonText = texts.inProgressPrivate;
          inProgressTooltip = texts.inProgressPrivateTooltip;
        } else {
          inProgressRequestedButtonText = texts.joinSession;
          inProgressTooltip = texts.joinSessionTooltip;
        }
        return (
          <MachinesListRemoteSupportRequestedButton
            buttonText={inProgressRequestedButtonText}
            onViewDetails={
              canJoinInProgress ? handleSTActiveButtonClick : undefined
            }
            onCancel={openSTCancelRequest}
            loading={stActiveClickLoading}
            status={machine?.status as MachinesListSupportSessionsStatusTypes}
            {...remoteActionProps}
            tooltip={remoteActionProps.tooltip || inProgressTooltip}
            allowInProgressViewDetails={canJoinInProgress}
            showJoinSessionIcon={canJoinInProgress}
            showCancelIcon={canRequestorCancelActiveInProgress}
          />
        );
      }
      if (
        machine?.status === MachinesListSupportSessionsStatusTypes.REQUESTED ||
        machine?.isRequested
      ) {
        return (
          <>
            <MachinesListRemoteSupportRequestedButton
              onViewDetails={(e) =>
                handleRequestButtonClick(
                  e,
                  MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
                )
              }
              onCancel={openSTCancelRequest}
              buttonText={texts.requested}
              {...remoteActionProps}
              loading={stRequestClickLoading}
              showCancelIcon={canRequestorCancelActiveInProgress}
            />
          </>
        );
      }
      if (!machine.gateway_id?.trim()) return null;

      const hasMachineId = Boolean(machine.machine_id?.trim());
      const requestButton = (
        <ButtonWithIcon
          fun={(e) => {
            if (!e) return;
            handleRequestButtonClick(
              e,
              MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
            );
          }}
          icon={machinesRemoteSupportIcon}
          text={texts.request}
          variant={["primary"]}
          disabled={!hasMachineId || isRemoteActionsDisabled || !hasVncSkill}
          disabledAppearance={
            isRemoteActionsDisabled || !hasVncSkill ? "sse" : undefined
          }
        />
      );

      const requestTooltip = !hasVncSkill
        ? texts.missingMachineSkillTooltip
        : remoteActionsDisabledTooltip;

      return hasMachineId ? (
        <CustomTooltip content={requestTooltip} maxWidth="360px">
          {requestButton}
        </CustomTooltip>
      ) : (
        <CustomTooltip
          content={
            remoteActionProps.tooltip || texts.machineConfigurationWarning
          }
          maxWidth="360px"
          className={
            styles[`${cssClassName}__configuration-warning-disabled-action`]
          }
        >
          {requestButton}
        </CustomTooltip>
      );
    }, [
      isClosingState,
      isDurationExpired,
      machine.status,
      machine?.remaining_duration,
      machine?.visibility,
      machine.isRequested,
      machine.gateway_id,
      setLocalRemainingDuration,
      texts.closing,
      texts.active,
      texts.inProgressSession,
      texts.requested,
      handleRequestButtonClick,
      openSTCancelRequest,
      texts.request,
      texts.machineConfigurationWarning,
      machine.machine_id,
      stRequestClickLoading,
      stActiveClickLoading,
      handleSTActiveButtonClick,
      disconnectActiveSession,
      disconnecting,
      enterScreensharing,
      texts.inProgressPrivate,
      texts.joinSession,
      texts.inProgressPrivateTooltip,
      texts.joinSessionTooltip,
      isCurrentUserSessionOwner,
      isCurrentUserSessionViewer,
      texts.viewing,
      texts.viewingTooltip,
      machineIdentity,
      onActiveOtherUserVisibilityChange,
      canRequestorCancelActiveInProgress,
      isRemoteActionsDisabled,
      remoteActionsDisabledTooltip,
      remoteActionProps,
    ]);

    return (
      <>
        <tr
          id={`machine-row-${machine.machine_id}`}
          className={cn(styles[`${cssClassName}__grid-row`], {
            [styles[
              `${cssClassName}__grid-row--manage-machines-columns-display`
            ]]: showSTSupport && showOTSupport,
            [styles[
              `${cssClassName}__grid-row--technician-or-operator-column-display`
            ]]: showSTSupport !== showOTSupport,
            [styles[
              `${cssClassName}__grid-row--no-permission-columns-display`
            ]]: !showSTSupport && !showOTSupport,
            [styles[`${cssClassName}__grid-row--selected`]]:
              targetMachineId === machine.machine_id,
          })}
        >
          <td className={styles[`${cssClassName}__grid-cell`]}>
            <span className={styles[`${cssClassName}__grid-cell-flex-wrapper`]}>
              <span
                className={cn(
                  styles[`${cssClassName}__description-text`],
                  styles[
                    `${cssClassName}__grid-cell-flex-wrapper--machine-name-container`
                  ],
                )}
              >
                {/* <IndicatorText
                  text=""
                  variant={isConnected ? "green" : "red"}
                /> */}
                <img src={sourceIcon} alt={texts.sourceIconAltText} />
                <EditMachineName machine={machine} />
              </span>
              <span
                className={cn(
                  styles[
                    `${cssClassName}__grid-cell-flex-wrapper--links-outer-wrapper`
                  ],
                )}
              >
                <span className={styles[`${cssClassName}__links-wrapper`]}>
                  <VerticalSeparator />
                  {
                    <>
                      <RenderIfUserPermissionsRulesMatch
                        mode="anyInEachGroup"
                        permissions={[
                          ["view-active-nodeset", "manage-active-nodeset"],
                          "view-initial-server-nodeset",
                        ]}
                      >
                        {hasOpcUaSkill && !actionDisabledReason ? (
                          <IconButton
                            fun={(e) =>
                              handleOpcUaActionClick(e, clickTreeStructureLink)
                            }
                            icon={treeStructureIcon}
                            tooltip={texts.goToTreeStructure}
                          />
                        ) : (
                          <CustomTooltip
                            content={
                              hasOpcUaSkill
                                ? texts.machineConfigurationWarning
                                : texts.missingMachineSkillTooltip
                            }
                            maxWidth="360px"
                          >
                            <IconButton
                              fun={() => undefined}
                              icon={treeStructureIcon}
                              disabled
                            />
                          </CustomTooltip>
                        )}
                      </RenderIfUserPermissionsRulesMatch>
                      <RenderIfUserHasPermission permission="view-dashboards">
                        {hasOpcUaSkill && !actionDisabledReason ? (
                          <IconButton
                            fun={(e) =>
                              handleOpcUaActionClick(e, clickDashboardLink)
                            }
                            icon={dashboardIcon}
                            tooltip={texts.goToDashboard}
                          />
                        ) : (
                          <CustomTooltip
                            content={
                              hasOpcUaSkill
                                ? texts.machineConfigurationWarning
                                : texts.missingMachineSkillTooltip
                            }
                            maxWidth="360px"
                          >
                            <IconButton
                              fun={() => undefined}
                              icon={dashboardIcon}
                              disabled
                            />
                          </CustomTooltip>
                        )}
                      </RenderIfUserHasPermission>
                      {isUserConditionMonitoringViewer() &&
                        (hasOpcUaSkill && !actionDisabledReason ? (
                          <IconButton
                            fun={(e) =>
                              handleOpcUaActionClick(
                                e,
                                clickConditionMonitoringLink,
                              )
                            }
                            icon={conditionMonitoringIcon}
                            tooltip={texts.goToConditionMonitoring}
                          />
                        ) : (
                          <CustomTooltip
                            content={
                              hasOpcUaSkill
                                ? texts.machineConfigurationWarning
                                : texts.missingMachineSkillTooltip
                            }
                            maxWidth="360px"
                          >
                            <IconButton
                              fun={() => undefined}
                              icon={conditionMonitoringIcon}
                              disabled
                            />
                          </CustomTooltip>
                        ))}
                    </>
                  }
                  <RenderIfUserHasPermission permission="create-tickets">
                    <TicketSupportForMachineRowIconButton
                      machineName={machine.name}
                      machineId={machine.machine_id}
                    />
                  </RenderIfUserHasPermission>
                  {/* <RenderIfUserHasPermission permission="view-machine-info-in-sources-list">
                    <VerticalSeparator />
                    <SourceInfoWithPopover sourceId={machine?.opc_ua_data_source_id} />
                  </RenderIfUserHasPermission> */}
                  {showOTA && (
                    <RenderIfUserHasMultiplePermissions
                      permissions={[
                        "initiate-update-process",
                        "approve-update-process",
                        "manage-firmware",
                      ]}
                    >
                      {hasOpcUaSkill && !actionDisabledReason ? (
                        <IconButton
                          fun={(e) =>
                            handleOpcUaActionClick(e, clickMachineSettingsLink)
                          }
                          icon={MachineSettingsIcon}
                          tooltip={texts.goToMachineSettings}
                        />
                      ) : (
                        <CustomTooltip
                          content={
                            hasOpcUaSkill
                              ? texts.machineConfigurationWarning
                              : texts.missingMachineSkillTooltip
                          }
                          maxWidth="360px"
                        >
                          <IconButton
                            fun={() => undefined}
                            icon={MachineSettingsIcon}
                            disabled
                          />
                        </CustomTooltip>
                      )}
                    </RenderIfUserHasMultiplePermissions>
                  )}
                  {showShotDataTable && (
                    <RenderIfUserHasPermission permission="view-dashboards">
                      {hasOpcUaSkill && !actionDisabledReason ? (
                        <IconButton
                          fun={(e) =>
                            handleOpcUaActionClick(e, clickShotDataTableLink)
                          }
                          icon={shotDataTableIcon}
                          tooltip={texts.goToShotDataTable}
                        />
                      ) : (
                        <CustomTooltip
                          content={
                            hasOpcUaSkill
                              ? texts.machineConfigurationWarning
                              : texts.missingMachineSkillTooltip
                          }
                          maxWidth="360px"
                        >
                          <IconButton
                            fun={() => undefined}
                            icon={shotDataTableIcon}
                            disabled
                          />
                        </CustomTooltip>
                      )}
                    </RenderIfUserHasPermission>
                  )}
                </span>
              </span>
            </span>
          </td>
          <td className={styles[`${cssClassName}__grid-cell`]}>
            {machine.machine_id ? (
              <button
                type="button"
                className={cn(
                  styles[`${cssClassName}__description-text`],
                  styles[`${cssClassName}__machine-id-copy-button`],
                )}
                aria-label={`${texts.copyTooltip}: ${machine.machine_id}`}
                onClick={handleCopyMachineId}
              >
                {machine.machine_id}
              </button>
            ) : (
              <span className={styles[`${cssClassName}__description-text`]}>
                {texts.noMachineId}
              </span>
            )}
          </td>
          <td className={styles[`${cssClassName}__grid-cell`]}>
            <span className={styles[`${cssClassName}__description-text`]}>
              {machine.hostname || texts.noIpAddress}
            </span>
          </td>
          <td className={styles[`${cssClassName}__grid-cell`]}>
            <span className={styles[`${cssClassName}__description-text`]}>
              {machine.vendor_id || machine.gateway_name}
            </span>
          </td>

          {showSTSupport && (
            <td className={styles[`${cssClassName}__grid-cell`]}>
              {STactionCell}
            </td>
          )}
          {showOTSupport && (
            <td className={styles[`${cssClassName}__grid-cell`]}>
              {OTactionCell}
            </td>
          )}
        </tr>

        {showOTCancelModal &&
          createPortal(
            <MachinesCancelOperatorTechnicianManagerModal
              onConfirm={confirmInterruptRemoteAccessRequest}
              onCancel={closeOTCancelModal}
              machineName={machine.name || machine.machine_id}
              companyName={companyName}
              isConnected={isConnected}
              modalTexts={modalTexts}
              loading={confirmCancelRemoteAccessRequestLoading}
              displayName={displayName || ""}
              disableConfirmAction={isRemoteActionsDisabled}
              disabledConfirmTooltip={remoteActionsDisabledTooltip}
            />,
            modalEntryPointReference,
          )}
        {showSTCancelModal &&
          createPortal(
            <MachinesCancelServiceTechnicianManagerModal
              onConfirm={confirmCancelRemoteAccessRequest}
              onCancel={closeSTCancelModal}
              machineName={machine?.name || machine?.machine_id}
              companyName={companyName}
              isConnected={isConnected}
              modalTexts={modalTexts}
              loading={confirmCancelRemoteAccessRequestLoading}
              disableConfirmAction={isRemoteActionsDisabled}
              disabledConfirmTooltip={remoteActionsDisabledTooltip}
            />,
            modalEntryPointReference,
          )}
        {showDisconnectSupportModal &&
          createPortal(
            <MachinesCancelRequestDisconnectSupportModal
              onCancel={closeDisconnectSupportModal}
              modalTexts={disconnectTexts}
            />,
            modalEntryPointReference,
          )}
      </>
    );
  },
);

MachineListRow.displayName = "MachineListRow";
